// Get API base URL from environment variable, with fallback to production URL
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) {
    // Remove all trailing slashes, then add /api if not already present
    let cleanUrl = envUrl.replace(/\/+$/, '');
    
    // Check if /api is already in the URL
    if (cleanUrl.endsWith('/api')) {
      return cleanUrl;
    }
    
    // Ensure single slash between base URL and /api
    if (!cleanUrl.endsWith('/')) {
      cleanUrl = cleanUrl + '/';
    }
    
    // Remove any double slashes that might occur
    return (cleanUrl + 'api').replace(/([^:]\/)\/+/g, '$1');
  }
  // Fallback to production URL
  return 'https://sayagainservices.sayagain.ai/api';
};

const API_BASE_URL = getApiBaseUrl();

console.log('API Base URL:', API_BASE_URL);

// Helper function to build URLs without double slashes
const buildUrl = (baseUrl, endpoint) => {
  const cleanBase = baseUrl.replace(/\/+$/, ''); // Remove trailing slashes
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`; // Ensure endpoint starts with /
  return `${cleanBase}${cleanEndpoint}`.replace(/([^:]\/)\/+/g, '$1'); // Remove double slashes (except after http:)
};

export const apiClient = {
  get: async (endpoint, token) => {
    if (!token) {
      throw new Error('No authentication token provided');
    }
    
    let response;
    try {
      const fullUrl = buildUrl(API_BASE_URL, endpoint);
      console.log('GET Request URL:', fullUrl);
      response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
    } catch (fetchError) {
      // Handle CORS and network errors
      if (fetchError.name === 'TypeError' && fetchError.message.includes('fetch')) {
        throw new Error('CORS Error: The API server is not allowing requests from this origin. Please contact the backend team to enable CORS for localhost:3000 and localhost:3002');
      }
      throw fetchError;
    }
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || response.statusText, status: response.status };
      }
      
      // Extract error message from different possible structures
      let errorMessage = `API Error: ${response.status} ${response.statusText}`;
      
      if (errorData.error) {
        errorMessage = typeof errorData.error === 'string' ? errorData.error : (errorData.error.message || JSON.stringify(errorData.error));
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.msg) {
        errorMessage = errorData.msg;
      } else if (typeof errorData === 'string') {
        errorMessage = errorData;
      }
      
      // Handle token-related errors
      if (response.status === 401 || response.status === 403) {
        // Clear invalid token
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        errorMessage = errorMessage || 'Authentication failed. Please login again.';
      }
      
      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = errorData;
      error.error = errorMessage; // Also set error property for easier access
      throw error;
    }
    return response.json();
  },

  post: async (endpoint, data, token) => {
    // Token is optional for registration endpoint
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    let response;
    try {
      const fullUrl = buildUrl(API_BASE_URL, endpoint);
      console.log('POST Request URL:', fullUrl);
      response = await fetch(fullUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(data)
      });
    } catch (fetchError) {
      // Handle CORS and network errors
      if (fetchError.name === 'TypeError' && fetchError.message.includes('fetch')) {
        throw new Error('CORS Error: The API server is not allowing requests from this origin. Please contact the backend team to enable CORS for localhost:3000 and localhost:3002');
      }
      throw fetchError;
    }
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || response.statusText, status: response.status };
      }
      
      // Extract error message from different possible structures
      let errorMessage = `API Error: ${response.status} ${response.statusText}`;
      
      if (errorData.error) {
        errorMessage = typeof errorData.error === 'string' ? errorData.error : (errorData.error.message || JSON.stringify(errorData.error));
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.msg) {
        errorMessage = errorData.msg;
      } else if (typeof errorData === 'string') {
        errorMessage = errorData;
      }
      
      // Handle token-related errors
      if (response.status === 401 || response.status === 403) {
        // Clear invalid token
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        errorMessage = errorMessage || 'Authentication failed. Please login again.';
      }
      
      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = errorData;
      error.error = errorMessage; // Also set error property for easier access
      throw error;
    }
    return response.json();
  },

  put: async (endpoint, data, token) => {
    if (!token) {
      throw new Error('No authentication token provided');
    }
    
    let response;
    try {
      const fullUrl = buildUrl(API_BASE_URL, endpoint);
      console.log('PUT Request URL:', fullUrl);
      response = await fetch(fullUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(data)
      });
    } catch (fetchError) {
      // Handle CORS and network errors
      if (fetchError.name === 'TypeError' && fetchError.message.includes('fetch')) {
        throw new Error('CORS Error: The API server is not allowing requests from this origin. Please contact the backend team to enable CORS for localhost:3000 and localhost:3002');
      }
      throw fetchError;
    }
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || response.statusText, status: response.status };
      }
      
      // Extract error message from different possible structures
      let errorMessage = `API Error: ${response.status} ${response.statusText}`;
      
      if (errorData.error) {
        errorMessage = typeof errorData.error === 'string' ? errorData.error : (errorData.error.message || JSON.stringify(errorData.error));
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.msg) {
        errorMessage = errorData.msg;
      } else if (typeof errorData === 'string') {
        errorMessage = errorData;
      }
      
      // Handle token-related errors
      if (response.status === 401 || response.status === 403) {
        // Clear invalid token
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        errorMessage = errorMessage || 'Authentication failed. Please login again.';
      }
      
      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = errorData;
      error.error = errorMessage; // Also set error property for easier access
      throw error;
    }
    return response.json();
  },

  delete: async (endpoint, token) => {
    if (!token) {
      throw new Error('No authentication token provided');
    }
    
    let response;
    try {
      const fullUrl = buildUrl(API_BASE_URL, endpoint);
      console.log('DELETE Request URL:', fullUrl);
      response = await fetch(fullUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
    } catch (fetchError) {
      // Handle CORS and network errors
      if (fetchError.name === 'TypeError' && fetchError.message.includes('fetch')) {
        throw new Error('CORS Error: The API server is not allowing requests from this origin. Please contact the backend team to enable CORS for localhost:3000 and localhost:3002');
      }
      throw fetchError;
    }
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || response.statusText, status: response.status };
      }
      
      // Extract error message from different possible structures
      let errorMessage = `API Error: ${response.status} ${response.statusText}`;
      
      if (errorData.error) {
        errorMessage = typeof errorData.error === 'string' ? errorData.error : (errorData.error.message || JSON.stringify(errorData.error));
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.msg) {
        errorMessage = errorData.msg;
      } else if (typeof errorData === 'string') {
        errorMessage = errorData;
      }
      
      // Handle token-related errors
      if (response.status === 401 || response.status === 403) {
        // Clear invalid token
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        errorMessage = errorMessage || 'Authentication failed. Please login again.';
      }
      
      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = errorData;
      error.error = errorMessage; // Also set error property for easier access
      throw error;
    }
    return response.json();
  }
};

export default API_BASE_URL;

